import { useState, useEffect, useCallback, useRef } from 'react';
import { marketingService, MarketingBroadcast } from '../services/marketing.service';
import { useMessageHandler } from './useMessageHandler';
import { useStateRef } from './useStateRef';

interface UseMarketingBroadcastsParams {
  limit?: number;
}

export const useMarketingBroadcasts = (params: UseMarketingBroadcastsParams = {}) => {
  const showMessage = useMessageHandler();
  
  const [broadcasts, , setBroadcasts] = useStateRef<MarketingBroadcast[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const cursorRef = useRef<string | undefined>(undefined);
  const isFirstLoadRef = useRef(true);
  const isFetchingRef = useRef(false);

  const pageSize = params.limit || 10;
  
  const filteredBroadcasts = broadcasts;
  
  const displayStartIndex = (currentPage - 1) * pageSize;
  const displayBroadcasts = filteredBroadcasts.slice(displayStartIndex, displayStartIndex + pageSize);
  const isDisplayFullSize = displayBroadcasts.length === pageSize;
  
  // Calculate if there's a next page:
  // - If current page is not full, there's no next page
  // - If current page is full AND we have a cursor, there MIGHT be a next page
  // - If current page is full but we have no cursor, there's no next page
  const canHaveNextPage = isDisplayFullSize && !!cursorRef.current;
  
  // Check if the next page would be empty by looking at our current data
  const nextPageStartIndex = currentPage * pageSize;
  const hasDataForNextPage = filteredBroadcasts.length > nextPageStartIndex;
  
  // Show "Next" button if:
  // 1. We have data for the next page already loaded, OR
  // 2. Current page is full AND we have a cursor (might have more data to fetch)
  const hasNext = hasDataForNextPage || canHaveNextPage;
  
  const needsMoreData = !isDisplayFullSize && !!cursorRef.current;

  /**
   * Fetch more broadcasts from backend with cursor
   */
  const fetchMore = useCallback(
    async () => {
      // Prevent concurrent fetches
      if (isFetchingRef.current) {
        console.log('[useMarketingBroadcasts] Fetch skipped - already fetching');
        return;
      }

      // Read cursor directly from ref to get latest value
      const cursor = cursorRef.current;
      
      console.log('[useMarketingBroadcasts] fetchMore called', {
        cursor: cursor ? 'present' : 'none',
      });

      isFetchingRef.current = true;
      setLoading(true);

      try {
        const response = await marketingService.getBroadcasts({
          limit: pageSize,
          ...(cursor && { lastEvaluatedKey: cursor })
        });

        // If we got broadcasts, add them to the list
        if (response.data && response.data.length > 0) {
          setBroadcasts(prev => [...prev, ...response.data]);
          cursorRef.current = response.pagination?.lastEvaluatedKey;
        } else {
          // If we got 0 broadcasts, clear the cursor
          // This will hide the "Next" button since there's no more data
          cursorRef.current = undefined;
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch broadcasts';
        showMessage({ error: errorMessage });
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
        setIsRefreshing(false);
      }
    },
    [pageSize, setBroadcasts, showMessage]
  );

  // Auto-fetch when needs more data OR when refreshing
  useEffect(() => {
    console.log('[useMarketingBroadcasts] Auto-fetch effect triggered', {
      isFirstLoad: isFirstLoadRef.current,
      needsMoreData,
      isFetching: isFetchingRef.current,
      isRefreshing,
      cursor: cursorRef.current ? 'present' : 'none',
    });
    
    // Auto-fetch on first load OR when we need more data to fill the current page OR when refreshing
    // The isFetchingRef prevents concurrent fetches
    if ((isFirstLoadRef.current || needsMoreData) && !isFetchingRef.current) {
      isFirstLoadRef.current = false;
      fetchMore();
    }
  }, [needsMoreData, fetchMore, isRefreshing]);

  /**
   * Refresh broadcasts (clear and reload)
   */
  const refresh = useCallback(() => {
    setIsRefreshing(true);
    cursorRef.current = undefined;
    isFirstLoadRef.current = true;
    isFetchingRef.current = false;
    setBroadcasts([]);
    setCurrentPage(1);
    // isRefreshing will be set to false after fetch completes
  }, [setBroadcasts]);

  // Pagination info
  const curItemsCount = filteredBroadcasts.length;
  const curPagesCount = Math.ceil(curItemsCount / pageSize);
  const curPageStartIndex = displayStartIndex + 1;
  const curPageEndIndex = displayStartIndex + displayBroadcasts.length;

  return {
    broadcasts: displayBroadcasts,
    allBroadcasts: filteredBroadcasts,
    loading: (loading && displayBroadcasts.length === 0) || isRefreshing,
    currentPage,
    setCurrentPage,
    hasMore: hasNext,
    totalPages: curPagesCount,
    totalItems: curItemsCount,
    pageStartIndex: curPageStartIndex,
    pageEndIndex: curPageEndIndex,
    refresh,
  };
};
